import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch } from '../store'

interface TimerEntry {
  id: string
  timestamp: number
  timeoutId?: number
}

interface TimerState {
  timerEntries: Record<string, TimerEntry>
}

export const TIMER_DURATION = 120000

const timerTimeouts: Record<string, number> = {}

const loadTimerEntries = (): Record<string, TimerEntry> => {
  try {
    const savedEntries = localStorage.getItem('timerEntries')
    return savedEntries ? JSON.parse(savedEntries) : {}
  } catch (error) {
    console.error('Failed to load timer entries from localStorage:', error)
    return {}
  }
}

const initialState: TimerState = {
  timerEntries: loadTimerEntries(),
}

export const timerSlice = createSlice({
  name: 'timer',
  initialState,
  reducers: {
    addTimerEntry: (state, action: PayloadAction<{ id: string }>) => {
      const id = action.payload.id
      state.timerEntries[id] = {
        id,
        timestamp: Date.now(),
      }
      localStorage.setItem('timerEntries', JSON.stringify(state.timerEntries))
    },
    removeTimerEntry: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload

      if (timerTimeouts[id]) {
        window.clearTimeout(timerTimeouts[id])
        delete timerTimeouts[id]
      }

      delete state.timerEntries[id]
      if (Object.keys(state.timerEntries).length > 0) {
        localStorage.setItem('timerEntries', JSON.stringify(state.timerEntries))
      } else {
        localStorage.removeItem('timerEntries')
      }
    },
  },
})

export const { addTimerEntry, removeTimerEntry } = timerSlice.actions

export const addTimerWithAutoDelete = (id: string) => (dispatch: AppDispatch) => {
  if (timerTimeouts[id]) {
    window.clearTimeout(timerTimeouts[id])
  }

  dispatch(addTimerEntry({ id }))

  const timeoutId = window.setTimeout(() => {
    dispatch(removeTimerEntry({ id }))
    delete timerTimeouts[id]
  }, TIMER_DURATION)

  timerTimeouts[id] = timeoutId
}

export default timerSlice.reducer
